(function () {
  'use strict';

  /**
   * VariantGalleryManager
   *
   * Swaps the product media gallery to images stored in the
   * `custom.variant_gallery` metafield (type: list.file_reference).
   *
   * Design principles:
   * - Subscribes to PUB_SUB_EVENTS.variantChange, which fires AFTER
   *   product-info.js has finished updateMedia / setActiveMedia / price /
   *   inventory / add-to-cart state. Zero conflict with default Shopify JS.
   * - Reads image data from an inline JSON block rendered by Liquid — zero
   *   extra HTTP requests on variant change.
   * - Keeps original product media items in the DOM (display:none) so that
   *   setActiveMedia can still find thumbnails via querySelector without
   *   crashing. Our standalone handlers manage all variant gallery interactions.
   * - Does NOT touch elements.thumbnails — original thumbnails remain hidden
   *   but findable, so media-gallery.js setActiveMedia never gets null.
   * - Does NOT touch the product form, variant selects, or cart JS, so OOS
   *   notification apps and other extensions work normally.
   */
  class VariantGalleryManager {
    constructor(productInfoEl) {
      this.el = productInfoEl;
      this.sectionId = productInfoEl.dataset.section;
      this.mediaGallery = productInfoEl.querySelector('media-gallery');
      this.galleryData = this._loadGalleryData();
      this._originalGalleryItems = null;
      this._originalThumbItems = null;
      this._isShowingVariantGallery = false;
      this._createdThumbnailSlider = null;
      this._slideChangedHandler = null;

      if (!this.galleryData) return;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange.bind(this));
      }

      this._handleInitialVariant();
    }

    // ─── Data loading ────────────────────────────────────────────────────────

    _loadGalleryData() {
      const el = document.getElementById('VariantGalleryData-' + this.sectionId);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    // ─── Initial page-load variant ───────────────────────────────────────────

    _handleInitialVariant() {
      // Prefer the server-rendered initial variant ID embedded in the JSON
      // (__initial__ is always the Liquid selected_or_first_available_variant.id).
      // Fall back to the URL param, then the hidden form input.
      const urlVariantId = new URLSearchParams(window.location.search).get('variant');
      const formInput = this.el.querySelector('input[name="id"]');
      const variantId = String(
        urlVariantId ||
        this.galleryData.__initial__ ||
        (formInput ? formInput.value : '') ||
        ''
      );
      if (variantId && this.galleryData[variantId]) {
        this._saveOriginalItems();
        this._applyVariantGallery(this.galleryData[variantId], variantId);
      }
    }

    // ─── Pubsub handler ──────────────────────────────────────────────────────

    _onVariantChange({ data: { sectionId, variant } }) {
      if (sectionId !== this.sectionId || !variant) return;
      const variantId = String(variant.id);
      const images = this.galleryData[variantId];
      if (images && images.length > 0) {
        this._saveOriginalItems();
        this._applyVariantGallery(images, variantId);
      } else if (this._isShowingVariantGallery) {
        this._restoreOriginalGallery();
      }
    }

    // ─── Save original DOM items (once, before first swap) ───────────────────

    _saveOriginalItems() {
      if (this._originalGalleryItems) return;
      const galleryList = this.el.querySelector('.product__media-list');
      const thumbList = this.el.querySelector('.thumbnail-list');
      if (galleryList) {
        this._originalGalleryItems = Array.from(
          galleryList.querySelectorAll('.product__media-item:not(.variant-gallery-item)')
        );
      }
      if (thumbList) {
        this._originalThumbItems = Array.from(
          thumbList.querySelectorAll('.thumbnail-list__item:not(.variant-gallery-thumbnail)')
        );
      }
    }

    // ─── Apply variant gallery ───────────────────────────────────────────────

    _applyVariantGallery(images, variantId) {
      const galleryList = this.el.querySelector('.product__media-list');
      if (!galleryList) return;

      (this._originalGalleryItems || []).forEach(item => {
        item.style.display = 'none';
        item.classList.remove('is-active');
      });

      galleryList.querySelectorAll('.variant-gallery-item').forEach(el => el.remove());

      images.forEach((img, i) => {
        galleryList.appendChild(this._buildGalleryItem(img, variantId, i));
      });

      const thumbList = this._applyVariantThumbnails(images, variantId);
      this._isShowingVariantGallery = true;

      if (thumbList) {
        this._installVariantThumbnailHandlers(thumbList);
      }

      this._scrollGalleryToStart();
    }

    _applyVariantThumbnails(images, variantId) {
      let thumbList = this.el.querySelector('.thumbnail-list');

      if (!thumbList && images.length > 1) {
        thumbList = this._createThumbnailContainer();
      }

      if (!thumbList) return null;

      (this._originalThumbItems || []).forEach(item => {
        item.style.display = 'none';
        item.classList.remove('is-active');
      });

      thumbList.querySelectorAll('.variant-gallery-thumbnail').forEach(el => el.remove());

      images.forEach((img, i) => {
        thumbList.appendChild(this._buildThumbnailItem(img, variantId, i));
      });

      // ThumbnailSlider.init() fires asynchronously (IntersectionObserver) and
      // hides the container when its static sliderItems snapshot has < 2 items.
      // Fix (timing-independent): overwrite sliderItems on the element instance
      // so init() reads the correct count whether it fires before or after us.
      // Also remove 'hidden' in case init() has already fired.
      const thumbnailSliderEl = thumbList.closest('thumbnail-slider');
      if (thumbnailSliderEl) {
        if (images.length > 1) {
          thumbnailSliderEl.sliderItems = thumbnailSliderEl.querySelectorAll('[id^="Slide-"]');
          thumbnailSliderEl.classList.remove('hidden');
          thumbnailSliderEl.style.width = images.length < 4 ? 'fit-content' : '';
          thumbnailSliderEl.style.margin = images.length < 4 ? '0 auto' : '';
          const sliderUl = thumbnailSliderEl.querySelector('[id^="Slider-"]');
          if (sliderUl) sliderUl.dataset.mediaCount = String(images.length);
        } else {
          thumbnailSliderEl.sliderItems = thumbnailSliderEl.querySelectorAll('[id^="Slide-"]');
          thumbnailSliderEl.classList.add('hidden');
        }
      }

      return thumbList;
    }

    // ─── Restore original gallery ────────────────────────────────────────────

    _restoreOriginalGallery() {
      const galleryList = this.el.querySelector('.product__media-list');
      const thumbList = this.el.querySelector('.thumbnail-list');

      if (galleryList) {
        galleryList.querySelectorAll('.variant-gallery-item').forEach(el => el.remove());
        (this._originalGalleryItems || []).forEach(item => {
          item.style.display = '';
        });
      }

      if (thumbList) {
        thumbList.querySelectorAll('.variant-gallery-thumbnail').forEach(el => el.remove());
        (this._originalThumbItems || []).forEach(item => {
          item.style.display = '';
        });
        // Re-apply correct visibility for the thumbnail-slider element:
        // if original items < 2, ThumbnailSlider.init() would have hidden it.
        const thumbnailSliderEl = thumbList.closest('thumbnail-slider');
        if (thumbnailSliderEl) {
          const originalCount = (this._originalThumbItems || []).length;
          if (originalCount < 2) {
            thumbnailSliderEl.classList.add('hidden');
          } else {
            thumbnailSliderEl.classList.remove('hidden');
          }
          const sliderUl = thumbnailSliderEl.querySelector('[id^="Slider-"]');
          if (sliderUl) sliderUl.dataset.mediaCount = String(originalCount);
        }
      }

      this._cleanupSlideChangedHandler();

      if (this._createdThumbnailSlider) {
        this._createdThumbnailSlider.remove();
        this._createdThumbnailSlider = null;
      }

      this._isShowingVariantGallery = false;
      this._scrollGalleryToStart();
    }

    // ─── Standalone thumbnail interactions ───────────────────────────────────

    _installVariantThumbnailHandlers(thumbList) {
      const viewer = this.mediaGallery && this.mediaGallery.elements
        ? this.mediaGallery.elements.viewer
        : this.el.querySelector('[id^="GalleryViewer"]');

      thumbList.querySelectorAll('[data-target]').forEach(item => {
        const btn = item.querySelector('button');
        if (!btn) return;
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener('click', () => {
          this._setVariantActiveMedia(item.dataset.target, thumbList, viewer);
        });
      });

      // Sync thumbnail highlight when the user swipes/scrolls the gallery
      this._cleanupSlideChangedHandler();
      if (viewer) {
        this._slideChangedHandler = (event) => {
          if (!event.detail || !event.detail.currentElement) return;
          const mediaId = event.detail.currentElement.dataset.mediaId;
          if (mediaId && mediaId.indexOf('-vg-') !== -1) {
            this._setVariantActiveThumbnail(mediaId, thumbList);
          }
        };
        viewer.addEventListener('slideChanged', this._slideChangedHandler);
      }
    }

    _cleanupSlideChangedHandler() {
      if (!this._slideChangedHandler) return;
      const viewer = this.mediaGallery && this.mediaGallery.elements
        ? this.mediaGallery.elements.viewer
        : this.el.querySelector('[id^="GalleryViewer"]');
      if (viewer) viewer.removeEventListener('slideChanged', this._slideChangedHandler);
      this._slideChangedHandler = null;
    }

    _setVariantActiveMedia(mediaId, thumbList, viewer) {
      if (!viewer) return;

      viewer.querySelectorAll('[data-media-id]').forEach(el => el.classList.remove('is-active'));
      const activeMedia = viewer.querySelector('[data-media-id="' + mediaId + '"]');
      if (activeMedia) {
        activeMedia.classList.add('is-active');
        const galleryList = viewer.querySelector('.product__media-list') || (viewer.slider || null);
        if (galleryList && typeof galleryList.scrollTo === 'function') {
          galleryList.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
        }
      }

      this._setVariantActiveThumbnail(mediaId, thumbList);
    }

    _setVariantActiveThumbnail(mediaId, thumbList) {
      if (!thumbList) return;
      thumbList.querySelectorAll('[id^="Slide-"]').forEach(el => {
        el.classList.remove('is-active');
        const btn = el.querySelector('button');
        if (btn) btn.removeAttribute('aria-current');
      });
      const activeThumbnail = thumbList.querySelector('[data-target="' + mediaId + '"]');
      if (activeThumbnail) {
        activeThumbnail.classList.add('is-active');
        const btn = activeThumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');
      }
    }

    // ─── Dynamically create thumbnail container ───────────────────────────────

    _createThumbnailContainer() {
      if (this._createdThumbnailSlider) {
        return this._createdThumbnailSlider.querySelector('.thumbnail-list');
      }

      const sliderEl = document.createElement('thumbnail-slider');
      sliderEl.id = 'GalleryThumbnails-' + this.sectionId;
      sliderEl.className = 'thumbnail-slider slider-mobile-gutter';

      const ul = document.createElement('ul');
      ul.id = 'Slider-Thumbnails-' + this.sectionId;
      ul.className = 'thumbnail-list list-unstyled slider slider--mobile slider--tablet-up';

      sliderEl.appendChild(ul);

      // Insert as a sibling of <use-animate> (or viewer), not inside it,
      // to match the position of the Liquid-rendered thumbnail-slider.
      const viewer = this.el.querySelector('[id^="GalleryViewer"]');
      const useAnimate = viewer && viewer.closest('use-animate');
      const anchor = useAnimate || viewer;
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(sliderEl, anchor.nextSibling);
      } else if (this.mediaGallery) {
        this.mediaGallery.appendChild(sliderEl);
      }

      this._createdThumbnailSlider = sliderEl;
      return ul;
    }

    // ─── DOM builders ─────────────────────────────────────────────────────────

    _buildGalleryItem(img, variantId, index) {
      const mediaId = this.sectionId + '-vg-' + img.id;
      const ratio = (img.height > 0 && img.width > 0)
        ? ((img.height / img.width) * 100).toFixed(4)
        : 100;

      const srcset = ['165', '360', '535', '750', '1100', '1500']
        .filter(w => img.urls && img.urls[w])
        .map(w => img.urls[w] + ' ' + w + 'w')
        .join(', ');

      const sizes = '(min-width: 1200px) 700px, (min-width: 990px) calc(55vw - 10rem), (min-width: 750px) calc((100vw - 10rem) / 2), calc(100vw - 3rem)';

      const li = document.createElement('li');
      li.id = 'Slide-' + this.sectionId + '-vg-' + img.id;
      li.className = 'product__media-item grid__item slider__slide variant-gallery-item' +
        (index === 0 ? ' is-active' : '');
      li.dataset.mediaId = mediaId;
      li.dataset.mediaPosition = String(index + 1);
      li.dataset.variantId = variantId;

      const wrapper = document.createElement('div');
      wrapper.className = 'media-wrapper';

      const media = document.createElement('div');
      media.className = 'product__media media media--adapt';
      media.style.setProperty('--image-ratio-percent', ratio + '%');

      const imgEl = document.createElement('img');
      imgEl.src = img.urls ? img.urls['1500'] : '';
      if (srcset) imgEl.srcset = srcset;
      imgEl.sizes = sizes;
      imgEl.alt = img.alt || '';
      imgEl.width = img.width || '';
      imgEl.height = img.height || '';
      imgEl.loading = index === 0 ? 'eager' : 'lazy';

      media.appendChild(imgEl);
      wrapper.appendChild(media);
      li.appendChild(wrapper);
      return li;
    }

    _buildThumbnailItem(img, variantId, index) {
      const mediaId = this.sectionId + '-vg-' + img.id;

      const li = document.createElement('li');
      li.id = 'Slide-Thumbnails-' + this.sectionId + '-vg-' + img.id;
      li.className = 'thumbnail-list__item slider__slide media-wrapper media-wrapper--small variant-gallery-thumbnail' +
        (index === 0 ? ' is-active' : '');
      li.dataset.target = mediaId;
      li.dataset.mediaPosition = String(index + 1);
      li.dataset.variantId = variantId;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'thumbnail media media--square';
      btn.setAttribute('aria-label', 'Load image ' + (index + 1));
      btn.setAttribute('aria-controls', 'GalleryViewer-' + this.sectionId);
      if (index === 0) btn.setAttribute('aria-current', 'true');

      const thumbImg = document.createElement('img');
      thumbImg.src = img.urls ? img.urls['thumb'] : '';
      thumbImg.alt = img.alt || '';
      thumbImg.loading = 'lazy';
      thumbImg.width = 54;
      thumbImg.height = 54;

      btn.appendChild(thumbImg);
      li.appendChild(btn);
      return li;
    }

    // ─── Scroll helper ────────────────────────────────────────────────────────

    _scrollGalleryToStart() {
      const viewer = this.el.querySelector('[id^="GalleryViewer"]');
      if (!viewer) return;
      const slider = viewer.slider || viewer.querySelector('.product__media-list');
      if (slider && typeof slider.scrollTo === 'function') {
        slider.scrollTo({ left: 0, behavior: 'instant' });
      }
    }
  }

  function init() {
    document.querySelectorAll('product-info[data-section]').forEach(function (el) {
      if (!el._variantGalleryManager) {
        el._variantGalleryManager = new VariantGalleryManager(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
